/**
 * Container for the Save button section of the extraction review view.
 *
 * Derives the list of categories with unresolved conflicts from the
 * live `extractedData`, manages the "Save with unresolved conflicts?"
 * confirmation dialog, calls {@link useSaveContractExtraction} on
 * confirm, and fires a success toast. On success the server flips the
 * extraction status to `SAVED` and the hook's refetch causes the
 * drawer to re-render in read-only mode — which unmounts this
 * container because the parent guards on `canSave`.
 *
 * Unresolved conflicts are determined by scanning each category's
 * items for `isConflict === true`. SE-4342's per-category
 * `resolveCategoryConflicts` mutation is the mechanism that clears
 * those flags server-side. The frontend does not persist any separate
 * "resolved" flag — it trusts the server state exclusively.
 * @module features/contracts/components/SaveExtractionSection/SaveExtractionSectionContainer
 */

import React, { memo, useCallback, useMemo, useState } from "react";

import { useIsContractOwner } from "@/features/contracts/hooks/useIsContractOwner";
import { useSaveContractExtraction } from "@/features/contracts/hooks/useSaveContractExtraction";
import type { ExtractionLineItem } from "@/features/contracts/types";
import { formatCategoryDisplayName } from "@/features/contracts/utils/formatCategoryDisplayName";

import SaveExtractionSectionView from "./SaveExtractionSectionView";

import { useToastNotification } from "@/hooks/useToastNotification";

/** Props for {@link SaveExtractionSectionContainer}. */
interface Props {
  /** UUID of the extraction being saved. */
  readonly extractionId: string;
  /** Raw extracted data keyed by category. */
  readonly extractedData: Record<string, unknown>;
  /** UUID of the player whose extraction is being saved. */
  readonly playerId: string;
  /** Document type of the extraction being saved. */
  readonly documentType: string;
  /**
   * When true, render only the button + dialog (no outer VStack
   * wrapping, no inline error text); errors are surfaced via toast.
   * Used by the drawer body to inline the Save button on the same row
   * as Edit and Discard (SE-4767).
   */
  readonly compact?: boolean;
}

// SE-4333 item E: route category-name formatting through the shared
// utility (`features/contracts/utils/formatCategoryDisplayName`) so
// every surface — drawer, mobile page, conflicts banner — speaks the
// same dialect for `fixedTerms` / `variable_bonuses` / etc. The
// inline formatter we used to keep here only handled snake_case,
// which surfaced as "FIXEDTERMS" once the `uppercase` CSS class on
// the tier header fired.

/**
 * Type guard that narrows a raw extractedData item to the canonical
 * `ExtractedTerm` shape.
 * @param item - Candidate value from a category array
 * @returns Whether the item has the canonical shape
 */
function isExtractionLineItem(item: unknown): item is ExtractionLineItem {
  if (typeof item !== "object" || item === null) return false;
  const candidate = item as Record<string, unknown>;
  return (
    typeof candidate.text === "string" &&
    typeof candidate.isConflict === "boolean" &&
    typeof candidate.confidenceNote === "string"
  );
}

/**
 * Returns the display names of categories whose items include at
 * least one with `isConflict === true`.
 * @param extractedData - Raw extractedData object
 * @returns Array of display category names (empty when no conflicts)
 */
function getUnresolvedCategoryNames(
  extractedData: Record<string, unknown>
): readonly string[] {
  return Object.keys(extractedData)
    .filter(key => {
      const raw = extractedData[key];
      if (!Array.isArray(raw)) return false;
      return raw.some(item => isExtractionLineItem(item) && item.isConflict);
    })
    .map(formatCategoryDisplayName);
}

/**
 * Container for the Save extraction section.
 * @param props - Component props
 * @param props.extractionId - UUID of the extraction being saved
 * @param props.extractedData - Raw extracted data keyed by category
 * @param props.playerId - Player UUID
 * @param props.documentType - Document type
 * @param props.compact - When true, render the button + dialog only and
 *   surface errors via toast (SE-4767 inline action row)
 * @returns The save section view bound to live state
 */
function SaveExtractionSectionContainer({
  extractionId,
  extractedData,
  playerId,
  documentType,
  compact = false,
}: Props) {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isOwner = useIsContractOwner();
  const { saveExtraction, isSaving } = useSaveContractExtraction();
  const toast = useToastNotification();

  const unresolvedCategoryNames = useMemo<readonly string[]>(
    () => getUnresolvedCategoryNames(extractedData),
    [extractedData]
  );

  const runSave = useCallback(async () => {
    setErrorMessage(null);
    const ok = await saveExtraction(extractionId, playerId, documentType);
    if (ok) {
      toast.show({
        description: "Contract data saved.",
        action: "success",
      });
      // Only flip dialog state when it was open. Calling the setter with
      // its current value would still trigger React 19 to schedule a
      // commit cycle, which races with component unmount during jest
      // teardown and surfaces as a null-throw — see jest.setup.ts.
      setIsConfirmDialogOpen(prev => (prev ? false : prev));
    } else {
      const message =
        "Couldn't save the extraction. Please try again in a moment.";
      // SE-4767: in compact mode the inline error <Text/> is suppressed
      // so the action row stays a single horizontal line; route the
      // failure through a toast instead so the user still sees it.
      if (compact) {
        toast.show({ description: message, action: "danger" });
      } else {
        setErrorMessage(message);
      }
    }
  }, [saveExtraction, extractionId, playerId, documentType, toast, compact]);

  const handleSavePress = useCallback(() => {
    if (unresolvedCategoryNames.length > 0) {
      setIsConfirmDialogOpen(true);
      return;
    }
    void runSave();
  }, [unresolvedCategoryNames, runSave]);

  const handleCancelConfirm = useCallback(() => {
    if (isSaving) return;
    setIsConfirmDialogOpen(false);
  }, [isSaving]);

  const handleConfirmSave = useCallback(() => {
    void runSave();
  }, [runSave]);

  // SE-4350 defense-in-depth: non-OWNERs never see the Save button.
  // Parent drawer already hides the whole Save section for them, but
  // this short-circuit makes the container safe to embed elsewhere.
  if (!isOwner) return null;

  return (
    <SaveExtractionSectionView
      isSaving={isSaving}
      isConfirmDialogOpen={isConfirmDialogOpen}
      unresolvedCategoryNames={unresolvedCategoryNames}
      errorMessage={errorMessage}
      onSavePress={handleSavePress}
      onCancelConfirm={handleCancelConfirm}
      onConfirmSave={handleConfirmSave}
      compact={compact}
    />
  );
}

SaveExtractionSectionContainer.displayName = "SaveExtractionSectionContainer";

export default memo(SaveExtractionSectionContainer);
